import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Edit, Trash2, Plus, Copy, Download, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import EventDialog from '@/components/EventDialog';
import ExpenseDialog from '@/components/ExpenseDialog';
import RevenueDialog from '@/components/RevenueDialog';
import ROICalculator from '@/components/ROICalculator';
import BoothPhotoGallery from '@/components/BoothPhotoGallery';
import DebriefForm from '@/components/DebriefForm';
import BusinessCards from '@/components/BusinessCards';
import ShowQualityMetrics from '@/components/ShowQualityMetrics';
import EventAnalyticsData from '@/components/EventAnalyticsData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [revenues, setRevenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [revenueDialogOpen, setRevenueDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editingRevenue, setEditingRevenue] = useState(null);

  useEffect(() => {
    fetchEventDetails();
  }, [id]);

  const fetchEventDetails = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [eventRes, expensesRes, revenuesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/events/${id}`, { headers }),
        axios.get(`${BACKEND_URL}/api/expenses?event_id=${id}`, { headers }),
        axios.get(`${BACKEND_URL}/api/revenues?event_id=${id}`, { headers }),
      ]);

      setEvent(eventRes.data);
      setExpenses(expensesRes.data);
      setRevenues(revenuesRes.data);
    } catch (error) {
      toast.error('Failed to load event details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.delete(`${BACKEND_URL}/api/events/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Event deleted successfully');
      navigate('/events');
    } catch (error) {
      toast.error('Failed to delete event');
      console.error(error);
    }
  };

  const handleDuplicate = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const response = await axios.post(`${BACKEND_URL}/api/events/${id}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Event duplicated successfully');
      navigate(`/events/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to duplicate event');
      console.error(error);
    }
  };

  const handleExportPDF = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const response = await axios.get(`${BACKEND_URL}/api/events/${id}/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${event.name.replace(/\s+/g, '_')}_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
      console.error(error);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    
    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.delete(`${BACKEND_URL}/api/expenses/${expenseId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Expense deleted');
      fetchEventDetails();
    } catch (error) {
      toast.error('Failed to delete expense');
      console.error(error);
    }
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseDialogOpen(true);
  };

  const handleDeleteRevenue = async (revenueId) => {
    if (!window.confirm('Are you sure you want to delete this revenue entry?')) return;
    
    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.delete(`${BACKEND_URL}/api/revenues/${revenueId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Revenue deleted');
      fetchEventDetails();
    } catch (error) {
      toast.error('Failed to delete revenue');
      console.error(error);
    }
  };

  const handleEditRevenue = (revenue) => {
    setEditingRevenue(revenue);
    setRevenueDialogOpen(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Parse date string directly without timezone conversion
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Event not found</p>
        <Button onClick={() => navigate('/events')} className="mt-4">
          Back to Events
        </Button>
      </div>
    );
  }

  const totalExpensesWithBooth = event.total_expenses + (event.booth_cost || 0);
  const profitLoss = event.total_revenue - totalExpensesWithBooth;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/events')}
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl sm:text-4xl font-bold font-heading tracking-tight truncate" data-testid="event-detail-title">
              {event.name}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">{event.location}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditDialogOpen(true)}
            data-testid="edit-event-button"
          >
            <Edit className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Edit</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDuplicate}
            data-testid="duplicate-event-button"
          >
            <Copy className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Duplicate</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportPDF}
            data-testid="export-pdf-button"
          >
            <Download className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Export</span>
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleDelete}
            data-testid="delete-event-button"
          >
            <Trash2 className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Delete</span>
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-1 sm:pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
            <CardTitle className="text-[10px] sm:text-sm text-muted-foreground uppercase tracking-wider">
              Revenue
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
            <p className="text-lg sm:text-3xl font-bold font-mono text-green-400" data-testid="total-revenue">
              {formatCurrency(event.total_revenue)}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-1 sm:pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
            <CardTitle className="text-[10px] sm:text-sm text-muted-foreground uppercase tracking-wider">
              Expenses
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
            <p className="text-lg sm:text-3xl font-bold font-mono text-red-400" data-testid="total-expenses">
              {formatCurrency(event.total_expenses)}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-1 sm:pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
            <CardTitle className="text-[10px] sm:text-sm text-muted-foreground uppercase tracking-wider">
              Profit/Loss
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
            <p
              className={`text-lg sm:text-3xl font-bold font-mono ${
                profitLoss >= 0 ? 'text-green-400' : 'text-red-400'
              }`}
              data-testid="profit-loss"
            >
              {formatCurrency(profitLoss)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="w-full overflow-x-auto flex justify-start sm:grid sm:grid-cols-11 sm:w-auto">
          <TabsTrigger value="overview" data-testid="tab-overview" className="text-xs sm:text-sm px-2 sm:px-4">Overview</TabsTrigger>
          <TabsTrigger value="financials" data-testid="tab-financials" className="text-xs sm:text-sm px-2 sm:px-4">Financials</TabsTrigger>
          <TabsTrigger value="sales" data-testid="tab-sales" className="text-xs sm:text-sm px-2 sm:px-4">Sales</TabsTrigger>
          <TabsTrigger value="quality" data-testid="tab-quality" className="text-xs sm:text-sm px-2 sm:px-4">Show Quality</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics" className="text-xs sm:text-sm px-2 sm:px-4">Analytics</TabsTrigger>
          <TabsTrigger value="roi" data-testid="tab-roi" className="text-xs sm:text-sm px-2 sm:px-4">ROI</TabsTrigger>
          <TabsTrigger value="logistics" data-testid="tab-logistics" className="text-xs sm:text-sm px-2 sm:px-4">Logistics</TabsTrigger>
          <TabsTrigger value="staff" data-testid="tab-staff" className="text-xs sm:text-sm px-2 sm:px-4">Staff</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts" className="text-xs sm:text-sm px-2 sm:px-4">Contacts</TabsTrigger>
          <TabsTrigger value="photos" data-testid="tab-photos" className="text-xs sm:text-sm px-2 sm:px-4">Photos</TabsTrigger>
          <TabsTrigger value="debrief" data-testid="tab-debrief" className="text-xs sm:text-sm px-2 sm:px-4">Debrief</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-6">
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle>Event Details</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                  Status
                </p>
                <p className="font-medium capitalize">
                  {event.application_status.replace('_', ' ')}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                  Approved & Paid
                </p>
                <p className="font-medium">{event.approved_paid ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                  Start Date
                </p>
                <p className="font-medium font-mono">{formatDate(event.start_date)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                  End Date
                </p>
                <p className="font-medium font-mono">{formatDate(event.end_date)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                  Setup Days
                </p>
                <p className="font-medium">{event.setup_days} days</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                  Power Cost
                </p>
                <p className="font-medium font-mono">{formatCurrency(event.power_cost)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                  Booth Cost
                </p>
                <p className="font-medium font-mono">{formatCurrency(event.booth_cost || 0)}</p>
              </div>
              {event.projected_revenue > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                    Projected Revenue
                  </p>
                  <p className="font-medium font-mono text-primary">{formatCurrency(event.projected_revenue)}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {event.notes && (
            <Card className="bg-card border-border/50">
              <CardHeader>
                <CardTitle>Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground whitespace-pre-wrap">{event.notes}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="financials" className="space-y-4 mt-6">
          {/* Financial Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="bg-green-500/10 border-green-500/30">
              <CardContent className="p-3 sm:p-4">
                <p className="text-xs text-muted-foreground uppercase">Total Revenue</p>
                <p className="text-lg sm:text-2xl font-bold text-green-400 font-mono">
                  {formatCurrency(event.total_revenue || 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-red-500/10 border-red-500/30">
              <CardContent className="p-3 sm:p-4">
                <p className="text-xs text-muted-foreground uppercase">Total Expenses</p>
                <p className="text-lg sm:text-2xl font-bold text-red-400 font-mono">
                  {formatCurrency(event.total_expenses || 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-purple-500/10 border-purple-500/30">
              <CardContent className="p-3 sm:p-4">
                <p className="text-xs text-muted-foreground uppercase">Booth Cost</p>
                <p className="text-lg sm:text-2xl font-bold text-purple-400 font-mono">
                  {formatCurrency(event.booth_cost || 0)}
                </p>
              </CardContent>
            </Card>
            <Card className={`${(event.total_revenue - event.total_expenses) >= 0 ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
              <CardContent className="p-3 sm:p-4">
                <p className="text-xs text-muted-foreground uppercase">Net Profit</p>
                <p className={`text-lg sm:text-2xl font-bold font-mono ${(event.total_revenue - event.total_expenses) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatCurrency((event.total_revenue || 0) - (event.total_expenses || 0))}
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-between items-center">
            <h3 className="text-xl sm:text-2xl font-semibold font-heading">Revenue</h3>
            <Button
              onClick={() => { setEditingRevenue(null); setRevenueDialogOpen(true); }}
              data-testid="add-revenue-button"
              size="sm"
              className="bg-primary text-primary-foreground"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Revenue
            </Button>
          </div>
          <Card className="bg-card border-border/50">
            <CardContent className="p-0">
              {revenues.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground" data-testid="no-revenues">
                  No revenue entries yet
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {revenues.map((rev) => (
                    <div key={rev.id} className="p-4 flex justify-between items-center" data-testid={`revenue-${rev.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium font-mono text-green-400">{formatCurrency(rev.amount)}</p>
                        <p className="text-sm text-muted-foreground truncate">{rev.notes || 'No notes'}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="text-sm text-muted-foreground font-mono">
                          {formatDate(rev.date)}
                        </p>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditRevenue(rev)}
                            data-testid={`edit-revenue-${rev.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDeleteRevenue(rev.id)}
                            data-testid={`delete-revenue-${rev.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-between items-center mt-8">
            <h3 className="text-2xl font-semibold font-heading">Expenses</h3>
            <Button
              onClick={() => { setEditingExpense(null); setExpenseDialogOpen(true); }}
              data-testid="add-expense-button"
              size="sm"
              className="bg-primary text-primary-foreground"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Expense
            </Button>
          </div>
          <Card className="bg-card border-border/50">
            <CardContent className="p-0">
              {expenses.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground" data-testid="no-expenses">
                  No expenses recorded yet
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {expenses.map((exp) => (
                    <div key={exp.id} className="p-4 flex justify-between items-center" data-testid={`expense-${exp.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{exp.category.replace('_', ' ').toUpperCase()}</p>
                        <p className="text-sm text-muted-foreground truncate">{exp.description}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-medium font-mono text-red-400">
                            {formatCurrency(exp.amount)}
                          </p>
                          <p className="text-sm text-muted-foreground font-mono">
                            {formatDate(exp.date)}
                          </p>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditExpense(exp)}
                            data-testid={`edit-expense-${exp.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDeleteExpense(exp.id)}
                            data-testid={`delete-expense-${exp.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales Tab - Product Sales for this Event */}
        <TabsContent value="sales" className="space-y-4 mt-6">
          <Card className="bg-card border-border/50">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Product Sales at This Event</CardTitle>
                <Button size="sm" onClick={() => navigate('/convention-sales')}>
                  <Plus className="h-4 w-4 mr-1" /> Add Sale
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-secondary/30 p-4 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Total Sales</p>
                  <p className="text-2xl font-bold text-green-400">{formatCurrency(event.total_revenue)}</p>
                </div>
                <div className="bg-secondary/30 p-4 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Total Expenses</p>
                  <p className="text-2xl font-bold text-red-400">{formatCurrency(totalExpensesWithBooth)}</p>
                </div>
                <div className="bg-secondary/30 p-4 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Net Profit</p>
                  <p className={`text-2xl font-bold ${profitLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {formatCurrency(profitLoss)}
                  </p>
                </div>
                <div className="bg-secondary/30 p-4 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Profit Margin</p>
                  <p className={`text-2xl font-bold ${profitLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {event.total_revenue > 0 ? Math.round((profitLoss / event.total_revenue) * 100) : 0}%
                  </p>
                </div>
              </div>
              
              <div className="border-t border-border pt-4">
                <h4 className="font-semibold mb-3">Sales Breakdown</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Gross Revenue</span>
                    <span className="text-green-400">{formatCurrency(event.total_revenue)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>− Booth Cost</span>
                    <span className="text-orange-400">({formatCurrency(event.booth_cost || 0)})</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>− Operating Expenses</span>
                    <span className="text-red-400">({formatCurrency(event.total_expenses)})</span>
                  </div>
                  <div className="flex justify-between font-bold pt-2 border-t border-border">
                    <span>Net Profit</span>
                    <span className={profitLoss >= 0 ? 'text-green-400' : 'text-red-400'}>
                      {formatCurrency(profitLoss)}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-primary/10 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  View detailed product sales, discounts, and analytics in the{' '}
                  <button onClick={() => navigate('/product-sales')} className="text-primary underline">
                    Sales Accounting
                  </button>{' '}
                  section.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Show Quality Tab */}
        <TabsContent value="quality" className="space-y-4 mt-6">
          <ShowQualityMetrics event={event} onUpdate={fetchEventDetails} />
        </TabsContent>

        {/* Analytics Data Tab */}
        <TabsContent value="analytics" className="space-y-4 mt-6">
          <EventAnalyticsData eventId={event.id} eventName={event.name} />
        </TabsContent>

        <TabsContent value="roi" className="space-y-4 mt-6">
          <ROICalculator event={event} />
        </TabsContent>

        <TabsContent value="logistics" className="space-y-4 mt-6">
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle>Travel & Logistics</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                  Departure Date
                </p>
                <p className="font-medium font-mono">
                  {formatDate(event.travel_departure_date)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                  Return Date
                </p>
                <p className="font-medium font-mono">
                  {formatDate(event.travel_return_date)}
                </p>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                  Trailer Info
                </p>
                <p className="font-medium">{event.trailer_info || 'N/A'}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                  Power/Electric Needs
                </p>
                <p className="font-medium">{event.power_electric_needs || 'N/A'}</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="staff" className="space-y-4 mt-6">
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle>Staff Working This Event</CardTitle>
            </CardHeader>
            <CardContent>
              {event.staff_working.length === 0 ? (
                <div className="text-center text-muted-foreground" data-testid="no-staff">
                  No staff assigned yet
                </div>
              ) : (
                <div className="space-y-2">
                  {event.staff_working.map((staff, index) => (
                    <div
                      key={index}
                      className="p-3 bg-secondary/30 rounded-lg"
                      data-testid={`staff-${index}`}
                    >
                      {staff}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contacts" className="space-y-4 mt-6">
          <Card className="bg-card border-border/50">
            <CardContent className="p-6">
              <BusinessCards eventId={id} eventName={event.name} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="photos" className="space-y-4 mt-6">
          <Card className="bg-card border-border/50">
            <CardContent className="p-6">
              <BoothPhotoGallery eventId={id} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="debrief" className="space-y-4 mt-6">
          <Card className="bg-card border-border/50">
            <CardContent className="p-6">
              <DebriefForm eventId={id} eventName={event.name} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <EventDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={() => {
          setEditDialogOpen(false);
          fetchEventDetails();
        }}
        event={event}
      />

      <ExpenseDialog
        open={expenseDialogOpen}
        onClose={() => { setExpenseDialogOpen(false); setEditingExpense(null); }}
        onSuccess={() => {
          setExpenseDialogOpen(false);
          setEditingExpense(null);
          fetchEventDetails();
        }}
        eventId={id}
        expense={editingExpense}
      />

      <RevenueDialog
        open={revenueDialogOpen}
        onClose={() => { setRevenueDialogOpen(false); setEditingRevenue(null); }}
        onSuccess={() => {
          setRevenueDialogOpen(false);
          setEditingRevenue(null);
          fetchEventDetails();
        }}
        eventId={id}
        revenue={editingRevenue}
      />
    </div>
  );
}
